import { useEffect, useState } from "react";
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { CONSENT_EVENT, getMarketingConsent } from "@/lib/consent";
import { OPEN_CONSENT_EVENT } from "@/components/ConsentBanner";
import { CartDrawer } from "@/components/CartDrawer";

export function FloatingCart({ raisedOnMobile = false, compactOnProduct = false }: { raisedOnMobile?: boolean; compactOnProduct?: boolean }) {
  const { totalItems } = useCart();
  const [open, setOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(raisedOnMobile && getMarketingConsent() === null);

  useEffect(() => {
    const openCart = () => setOpen(true);
    window.addEventListener('proton:open-cart', openCart);
    return () => window.removeEventListener('proton:open-cart', openCart);
  }, []);

  useEffect(() => {
    if (!raisedOnMobile) return;
    const openConsent = () => setConsentOpen(true);
    const syncConsent = () => setConsentOpen(getMarketingConsent() === null);
    window.addEventListener(OPEN_CONSENT_EVENT, openConsent);
    window.addEventListener(CONSENT_EVENT, syncConsent);
    return () => {
      window.removeEventListener(OPEN_CONSENT_EVENT, openConsent);
      window.removeEventListener(CONSENT_EVENT, syncConsent);
    };
  }, [raisedOnMobile]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label={`Open cart (${totalItems} items)`}
          className={`fixed right-4 z-[60] h-12 w-12 touch-manipulation rounded-full border border-primary-foreground/40 bg-primary text-primary-foreground shadow-[0_10px_28px_-8px_hsl(var(--primary)/0.65)] hover:bg-primary lg:h-14 lg:w-14 ${compactOnProduct ? "hidden lg:flex" : "flex"} ${raisedOnMobile ? `bottom-24 lg:bottom-6 ${consentOpen ? "hidden" : ""}` : "bottom-[max(1rem,env(safe-area-inset-bottom))] lg:right-6"}`}
          size="icon"
        >
          <ShoppingCart className="h-5 w-5 lg:h-6 lg:w-6" strokeWidth={2.25} />
          {totalItems > 0 && <Badge className="absolute -right-1.5 -top-1.5 flex h-6 min-w-6 items-center justify-center border-2 border-background bg-destructive px-1 text-xs font-bold text-destructive-foreground">{totalItems}</Badge>}
        </Button>
      </SheetTrigger>
      <CartDrawer close={() => setOpen(false)} />
    </Sheet>
  );
}